import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { GeneralDialog, GeneralInput, GeneralSelect, LoadingIndicator, TimeSelectorRow } from 'sharedComponents';
import { IconSuccess, IconWarningType } from 'svgIcons/MotionPortalIcons';
import colors from 'theme/_colors.scss';
import { SMART_SENSOR_DCU, EventTypeGatewayApplicability } from '../constants';
import GatewayEventTypeFilter from '../GatewayEventsFilter/GatewayEventTypeFilter';
import GatewayEventReportTypeFilter from '../GatewayEventsFilter/GatewayEventReportTypeFilter';

import { useDownloadLogsModal } from './useDownloadLogsModal';
import {
    DownloadLogsModalStatus,
    DownloadLogsReportingPeriod,
    DownloadLogsBannerVariant,
    DownloadLogsLoaderSize,
    DownloadLogsDefaultLimitInBytes,
    DownloadLogsDefaultMaxFileCount,
    DownloadLogsTextFields
} from './constants';
import { formatCountText, formatSizeText, formatDownloadLimitText, formatMaxFileCountText, isFileCountLimitExceeded } from './helpers';
import './style.scss';

const StatusBanner = ({ variant, icon, title, message, className = '' }) =>
    <div className={`download-logs-banner download-logs-banner--${variant} ${className}`}>
        {icon && <div className='download-logs-banner__icon'>{icon}</div>}
        <div className='download-logs-banner__text'>
            {title && <div className='download-logs-banner__title'>{title}</div>}
            {message && <div className='download-logs-banner__message'>{message}</div>}
        </div>
    </div>
    ;

StatusBanner.propTypes = {
    variant: PropTypes.oneOf(Object.values(DownloadLogsBannerVariant)).isRequired,
    icon: PropTypes.node,
    title: PropTypes.string,
    message: PropTypes.string,
    className: PropTypes.string
};

const DownloadLogsModalComponent = ({ show, onClose, tabType, organizationOptions = [] }) => {
    const { t: translate } = useTranslation();
    const isGatewayTab = tabType === SMART_SENSOR_DCU;

    const {
        formState,
        status,
        checkResult,
        updateField,
        toggleField,
        handleSelectDate,
        handleCheckDownloadLimit,
        handleDownload,
        handleRetryDownload,
        isFormValid
    } = useDownloadLogsModal(show, tabType, onClose);

    const isChecking = status === DownloadLogsModalStatus.CHECKING;
    const isPreparing = status === DownloadLogsModalStatus.PREPARING;
    const isTimeout = status === DownloadLogsModalStatus.TIMEOUT;
    const fieldsDisabled = isChecking || isPreparing;
    const hasNoFiles = checkResult?.numberOfFiles === 0;

    const renderSummaryRow = (label, value) =>
        <div className='download-logs-summary-row'>
            <span className='download-logs-summary-row__label'>{label}</span>
            <span className='download-logs-summary-row__value'>{value}</span>
        </div>
        ;

    const renderTextField = (key) => {
        const field = DownloadLogsTextFields.find((textField) => textField?.key === key);
        if (!field || !field?.appliesTo?.includes(tabType)) {
            return null;
        }
        return (
            <div className='download-logs-field'>
                <label>{translate(field?.labelKey)}</label>
                <GeneralInput
                    id={field?.id}
                    value={formState[field?.key]}
                    onChange={(value) => updateField(field?.key, value)}
                    onClear={() => updateField(field?.key, '')}
                    placeholder={translate(field?.placeholderKey)}
                    disabled={fieldsDisabled}
                />
            </div>
        );
    };

    const renderResultSection = () => {
        switch (status) {
            case DownloadLogsModalStatus.CHECKING:
                return (
                    <div className='download-logs-status download-logs-status--loading'>
                        <LoadingIndicator
                            size={DownloadLogsLoaderSize.CALCULATING}
                            placement='initial'
                            text={translate('ABB.Powertrain.Frontend.downloadLogsCalculatingMessage')}
                        />
                    </div>
                );
            case DownloadLogsModalStatus.READY:
                return hasNoFiles
                    ? <StatusBanner
                        variant={DownloadLogsBannerVariant.WARNING}
                        message={translate('ABB.Powertrain.Frontend.downloadLogsNoFilesFoundMessage')}
                        className='download-logs-banner--no-files'
                    />
                    : <StatusBanner
                        variant={DownloadLogsBannerVariant.SUCCESS}
                        icon={<IconSuccess width={16} height={16} color={colors.jungleGreen} />}
                        message={translate('ABB.Powertrain.Frontend.downloadLogsWithinLimitsMessage')}
                    />;
            case DownloadLogsModalStatus.EXCEEDED:
                return hasNoFiles
                    ? <StatusBanner
                        variant={DownloadLogsBannerVariant.WARNING}
                        message={translate('ABB.Powertrain.Frontend.downloadLogsNoFilesFoundMessage')}
                        className='download-logs-banner--no-files'
                    />
                    : <StatusBanner
                        variant={DownloadLogsBannerVariant.WARNING}
                        icon={<IconWarningType width={16} height={16} color={colors.deionizerBlack} />}
                        message={translate(isFileCountLimitExceeded(checkResult)
                            ? 'ABB.Powertrain.Frontend.downloadLogsFileCountExceededMessage'
                            : 'ABB.Powertrain.Frontend.downloadLogsLimitExceededMessage')}
                    />;
            case DownloadLogsModalStatus.PREPARING:
                return (
                    <div className='download-logs-status download-logs-status--preparing'>
                        <LoadingIndicator size={DownloadLogsLoaderSize.PREPARING} placement='initial' />
                        <div className='download-logs-status__title'>{translate('ABB.Powertrain.Frontend.downloadLogsPreparingTitle')}</div>
                        {renderSummaryRow(
                            translate('ABB.Powertrain.Frontend.downloadLogsNumberOfFilesLabel'),
                            formatCountText(checkResult?.numberOfFiles)
                        )}
                        {renderSummaryRow(
                            translate('ABB.Powertrain.Frontend.downloadLogsDownloadSizeLabel'),
                            formatSizeText(checkResult?.estimatedTotalDownLoadSizeInBytes)
                        )}
                        <div className='download-logs-status__subtext'>{translate('ABB.Powertrain.Frontend.downloadLogsPreparingSubtext')}</div>
                    </div>
                );
            case DownloadLogsModalStatus.TIMEOUT:
                return checkResult?.isDownLoadLimitExceededInBytes
                    ? <StatusBanner
                        variant={DownloadLogsBannerVariant.SUCCESS}
                        icon={<IconSuccess width={16} height={16} color={colors.jungleGreen} />}
                        message={translate('ABB.Powertrain.Frontend.downloadLogsWithinLimitsMessage')}
                    />
                    : <StatusBanner
                        variant={DownloadLogsBannerVariant.WARNING}
                        icon={<IconWarningType width={16} height={16} color={colors.deionizerBlack} />}
                        message={translate('ABB.Powertrain.Frontend.downloadLogsLimitExceededMessage')}
                    />;
            default:
                return (
                    <div className='download-logs-status download-logs-status--hint'>
                        {translate('ABB.Powertrain.Frontend.downloadLogsCheckLimitPromptMessage')}
                    </div>
                );
        }
    };

    const getAcceptButtonProps = () => {
        switch (status) {
            case DownloadLogsModalStatus.CHECKING:
                return {
                    text: translate('ABB.Powertrain.Frontend.downloadLogsCheckLimitButton'),
                    disabled: true,
                    className: 'download-logs-check-limit-button'
                };
            case DownloadLogsModalStatus.READY:
                return {
                    text: translate('ABB.Powertrain.Frontend.downloadLogsDownloadButton'),
                    onClick: handleDownload
                };
            case DownloadLogsModalStatus.PREPARING:
                return null;
            case DownloadLogsModalStatus.TIMEOUT:
                return {
                    text: translate('ABB.Powertrain.Frontend.downloadLogsRetryButton'),
                    onClick: handleRetryDownload
                };
            case DownloadLogsModalStatus.EXCEEDED:
            default:
                return {
                    text: translate('ABB.Powertrain.Frontend.downloadLogsCheckLimitButton'),
                    onClick: handleCheckDownloadLimit,
                    disabled: !isFormValid,
                    className: 'download-logs-check-limit-button'
                };
        }
    };

    return (
        <GeneralDialog
            modalClassName='download-logs-modal-container'
            dialogClassName='download-logs-modal'
            show={show}
            close={onClose}
            closeButton={!isPreparing}
            persistent
            hideFooter={isPreparing}
            title={translate('ABB.Powertrain.Frontend.downloadLogsLabel')}
            cancelButtonProps={{ text: translate('ABB.Powertrain.Frontend.cancelLabel'), disabled: isPreparing }}
            acceptButtonProps={getAcceptButtonProps()}
        >
            {isTimeout &&
                <StatusBanner
                    variant={DownloadLogsBannerVariant.WARNING}
                    icon={<IconWarningType width={16} height={16} color={colors.orangePeel} />}
                    message={translate('ABB.Powertrain.Frontend.downloadLogsTimeoutMessage')}
                    className='download-logs-banner--top'
                />
            }
            {!isPreparing && <div className='download-logs-modal__fields'>
                {isGatewayTab &&
                    <div className='download-logs-field'>
                        <label>{translate('ABB.Powertrain.Frontend.downloadLogsLogTypeLabel')}</label>
                        <GatewayEventTypeFilter
                            selectedStates={formState.logTypes}
                            selectState={(value) => toggleField('logTypes', value)}
                            gatewayEventLogType={EventTypeGatewayApplicability.SMART_SENSOR_DCU}
                            disabled={fieldsDisabled}
                            showLabels
                        />
                    </div>
                }
                {isGatewayTab &&
                    <div className='download-logs-field'>
                        <label>{translate('ABB.Powertrain.Frontend.downloadLogsReportTypeLabel')}</label>
                        <GatewayEventReportTypeFilter
                            selectedTypes={formState.reportTypes}
                            onSelectType={(value) => toggleField('reportTypes', value)}
                            disabled={fieldsDisabled}
                        />
                    </div>
                }
                {renderTextField('fileName')}
                {isGatewayTab && organizationOptions?.length > 0 &&
                    <div className='download-logs-field'>
                        <label>{translate('ABB.Powertrain.Frontend.gatewayManagementOrganizationName')}</label>
                        <GeneralSelect
                            inputId='download-logs-modal-organization-select'
                            options={organizationOptions}
                            value={organizationOptions.find(option => option.value === formState.organizationId) || null}
                            onChange={(selected) => updateField('organizationId', selected ? selected.value : null)}
                            isClearable
                            isDisabled={fieldsDisabled}
                            placeholder={translate('ABB.Powertrain.Frontend.select')}
                        />
                    </div>
                }
                {renderTextField('eventName')}
                {renderTextField('dcuSerialNumber')}
                {!isGatewayTab &&
                    <div className='download-logs-field download-logs-field--inline'>
                        <label>{translate('ABB.Powertrain.Frontend.eventListFilterEventTypesLbl')}</label>
                        <GatewayEventTypeFilter
                            selectedStates={formState.severityTypes}
                            selectState={(value) => toggleField('severityTypes', value)}
                            gatewayEventLogType={EventTypeGatewayApplicability.SMART_SENSOR}
                            disabled={fieldsDisabled}
                            showLabels={false}
                            showInfoIcon={false}
                        />
                    </div>
                }
                <div className='download-logs-field'>
                    <label>{translate('ABB.Powertrain.Frontend.downloadLogsReportingPeriodLabel')}</label>
                    <div className='filter-time-selector'>
                        <TimeSelectorRow
                            fromDate={formState?.dateSelection?.from}
                            toDate={formState?.dateSelection?.to}
                            handleSelectDate={handleSelectDate}
                            label={''}
                            maxDaysRange={DownloadLogsReportingPeriod.MAX_DAYS_RANGE}
                            maxDaysBack={DownloadLogsReportingPeriod.MAX_DAYS_BACK}
                            preSelectedDays={DownloadLogsReportingPeriod.PRESELECTED_DAYS}
                            numberOfMonths={2}
                            showLoading={false}
                            footerIntervalText={translate('ABB.Powertrain.Frontend.datePickerMax90DaysNote')}
                            disabled={fieldsDisabled}
                        />
                    </div>
                </div>
            </div>}
            <div className='download-logs-modal__result'>
                {!isPreparing && renderSummaryRow(
                    translate('ABB.Powertrain.Frontend.downloadLogsNumberOfFilesLabel'),
                    `${formatCountText(checkResult?.numberOfFiles)} / ${formatMaxFileCountText(checkResult?.maxFileCountForDownload, formatCountText(DownloadLogsDefaultMaxFileCount))}`
                )}
                {!isPreparing && renderSummaryRow(
                    translate('ABB.Powertrain.Frontend.downloadLogsDownloadSizeLabel'),
                    // Size isn't estimated by the backend once the file count limit is already exceeded.
                    `${isFileCountLimitExceeded(checkResult) ? formatSizeText(null) : formatSizeText(checkResult?.estimatedTotalDownLoadSizeInBytes)} / ${formatDownloadLimitText(checkResult?.downLoadLimitInBytes, formatSizeText(DownloadLogsDefaultLimitInBytes))}`
                )}
                {renderResultSection()}
            </div>
        </GeneralDialog>
    );
};

DownloadLogsModalComponent.propTypes = {
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    tabType: PropTypes.string.isRequired,
    organizationOptions: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        label: PropTypes.string
    }))
};

export default DownloadLogsModalComponent;
